import React, { memo, useState } from "react";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Collapse,
  Container,
  Divider,
  Drawer,
  Grid2,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  AccountCircle,
  CloseRounded,
  ExpandLess,
  ExpandMore,
  Favorite,
  History as HistoryIcon,
  // i want a map pin icon
  LocationOn,
  KeyboardArrowDown,
  Logout as LogoutIcon,
  MenuOutlined,
} from "@mui/icons-material";
import logo from "../../Assets/images/logo.svg";
import { appMenuPageConst } from "./AppHeader.const";
import APP_ROUTES from "../../Configurations/APP_ROUTES";
import { FullPaddingWrapper } from "../FullPaddingWrapper/FullPaddingWrapper";
import NotificationBell from "../NotificationBell/NotificationBell";
import { useDispatch, useSelector } from "react-redux";
import { logout, setToggleAuthModal } from "../../Redux/authReducer";
import { RootState } from "../../Store/store";
import toast from "react-hot-toast";
import { googleLogout } from "@react-oauth/google";

const AppHeader = memo((props) => {
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [propertiesAnchorEl, setPropertiesAnchorEl] = useState<null | HTMLElement>(null);
  const [mobilePropertiesOpen, setMobilePropertiesOpen] = useState<boolean>(false);
  const [avatarAnchorEl, setAvatarAnchorEl] = useState<null | HTMLElement>(null);
  const propertiesMenuOpen = Boolean(propertiesAnchorEl);
  const avatarMenuOpen = Boolean(avatarAnchorEl);

  const { user, isAuthenticated, wishlist } = useSelector(
    (state: RootState) => state?.auth || {}
  );

  // Get user's claimed/verified properties
  const propertyCollections = user?.propertyCollections || [];
  const hasClaimedProperties = checkIsAuth() && propertyCollections.length > 0;

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const isActiveRoute = (navigateTo: string) => {
    if (!navigateTo) return false;
    // Handle home route
    if (navigateTo === "" || navigateTo === "/") {
      return location.pathname === "/" || location.pathname === "";
    }
    return location.pathname === navigateTo;
  };

  function checkIsAuth() {
    if (
      isAuthenticated &&
      user &&
      Object.keys(user! || {}) &&
      Object.keys(user!).length > 0
    )
      return true;
    else return false;
  }

  const handlePropertiesClick = (event: React.MouseEvent<HTMLElement>) => {
    setPropertiesAnchorEl(event.currentTarget);
  };

  const handlePropertiesClose = () => {
    setPropertiesAnchorEl(null);
  };

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAvatarAnchorEl(event.currentTarget);
  };

  const handleAvatarClose = () => {
    setAvatarAnchorEl(null);
  };

  const handlePropertySelect = (property: any) => {
    handlePropertiesClose();
    // Use the REAPI propertyId for the URL, not MongoDB _id
    const propertyId = property?.propertyId?.propertyId || property?.propertyId?._id;
    if (!propertyId) {
      toast.error("Property not found");
      return;
    }
    navigate(`/${propertyId}/property-id/${propertyId}`);
  };

  const getPropertyAddress = (property: any) => {
    const address = property?.propertyId?.propertyInfo?.address;
    if (!address) return "Property";
    // Return a shortened version of the address
    const fullAddress = `${address.street || ""} ${address.city || ""}, ${address.state || ""}`.trim();
    return fullAddress || "Property";
  };

  const handelNavigate = (navigateObj: any) => {
    navigate(navigateObj.navigateTo);
    setOpenDrawer(false);
  };

  const handelLogin = () => {
    dispatch(setToggleAuthModal(true));
  };

  const handelLogout = () => {
    navigate("/");
    googleLogout();
    toast("Logout");
    dispatch(logout());
  };

  // return null

  return (
    <Container maxWidth={"xl"} disableGutters>
      <FullPaddingWrapper>
        <Box
          p={{
            lg: "0px 20px 0px 20px",
          }}
        >
          {/* Drawer Start */}
          <Box>
            <Drawer
              open={openDrawer}
              color="primary"
              onClose={() => setOpenDrawer(false)}
            >
              <Box flex={1}>
                <Box width={"300px"}>
                  <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    p={6}
                    py={5}
                    borderBottom={"1px solid #F0F0F0"}
                  >
                    <Typography
                      fontSize={{
                        lg: 40,
                        md: 12,
                        sm: 20,
                        xs: 20,
                      }}
                      fontWeight={"bolder"}
                    >
                      <Link to={"/"} onClick={() => setOpenDrawer(false)} aria-label="SimplerHome home">
                        <img src={logo} width={"90%"} alt="simplerhome" />
                      </Link>
                    </Typography>

                    <IconButton onClick={() => setOpenDrawer(false)}>
                      <CloseRounded
                        color="primary"
                        fontSize="inherit"
                        sx={{ fontSize: 30 }}
                      />
                    </IconButton>
                  </Stack>

                  <Stack mt={5}>
                    <List disablePadding>
                      {appMenuPageConst.map((text: any, index: Number) => {
                        const isActive = isActiveRoute(text.navigateTo);
                        return (
                          <ListItem
                            onClick={() => handelNavigate(text)}
                            key={text.header}
                            disableGutters
                            disablePadding
                          >
                            <ListItemButton
                              sx={{
                                color: isActive ? "primary.main" : "inherit",
                                fontWeight: isActive ? 700 : 400,
                                "& .MuiTypography-root": {
                                  fontWeight: isActive ? 700 : 400,
                                },
                              }}
                            >
                              {text.header}
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                    {/* My Properties - only shown when user has claimed properties */}
                    {hasClaimedProperties && (
                      <List disablePadding>
                        <ListItem disableGutters disablePadding>
                          <ListItemButton onClick={() => setMobilePropertiesOpen(!mobilePropertiesOpen)}>
                            My Properties
                            {mobilePropertiesOpen ? <ExpandLess sx={{ ml: "auto" }} /> : <ExpandMore sx={{ ml: "auto" }} />}
                          </ListItemButton>
                        </ListItem>
                        <Collapse in={mobilePropertiesOpen} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {propertyCollections.map((property: any, idx: number) => (
                              <ListItemButton
                                key={property?.propertyId?._id || idx}
                                sx={{ pl: 4 }}
                                onClick={() => {
                                  handlePropertySelect(property);
                                  setOpenDrawer(false);
                                }}
                              >
                                <LocationOn sx={{ mr: 1 }} />
                                <Typography variant="body2" noWrap>
                                  {getPropertyAddress(property)}
                                </Typography>
                              </ListItemButton>
                            ))}
                          </List>
                        </Collapse>
                      </List>
                    )}
                    {checkIsAuth() && (
                      <ListItem disableGutters disablePadding>
                        <ListItemButton
                          onClick={() => {
                            navigate("/wishlist");
                            setOpenDrawer(false);
                          }}
                        >
                          {/* <Favorite sx={{ mr: 1 }} /> */}
                          My Wishlist ({wishlist?.length || 0})
                        </ListItemButton>
                      </ListItem>
                    )}
                    {hasClaimedProperties && (
                      <ListItem disableGutters disablePadding>
                        <ListItemButton
                          onClick={() => {
                            navigate(String(APP_ROUTES.HOME_HISTORY.pathname));
                            setOpenDrawer(false);
                          }}
                        >
                          Home History
                        </ListItemButton>
                      </ListItem>
                    )}
                    {checkIsAuth() && (
                      <ListItem disableGutters disablePadding>
                        <ListItemButton
                          onClick={() => {
                            navigate(String(APP_ROUTES.MY_ACCOUNT.pathname));
                            setOpenDrawer(false);
                          }}
                        >
                          My Account
                        </ListItemButton>
                      </ListItem>
                    )}
                    <ListItem>
                      {checkIsAuth() ? (
                        <Button
                          sx={{ mt: 3 }}
                          fullWidth
                          variant="contained"
                          size="medium"
                          onClick={handelLogout}
                        >
                          Logout
                        </Button>
                      ) : (
                        <Button
                          sx={{ mt: 5 }}
                          fullWidth
                          variant="contained"
                          size="medium"
                          onClick={handelLogin}
                        >
                          Login
                        </Button>
                      )}
                    </ListItem>
                  </Stack>
                </Box>
              </Box>
              <Box p={5}>
                <Typography sx={{ opacity: "0.5" }}>
                  © {new Date().getFullYear()} SimplerHome
                </Typography>
              </Box>
            </Drawer>
          </Box>
          {/* Drawer End */}

          <AppBar
            elevation={0}
            color="transparent"
            position="static"
            sx={{ top: 0 }}
          >
            <Box id="header-container-mobile-only">
              {/* mobile-only-xs-only */}
              <Grid2
                container
                display={{
                  xl: "none",
                  lg: "none",
                  md: "none",
                  sm: "flex",
                  xs: "flex",
                }}
              >
                <Grid2 container size={{ xs: 12 }} alignItems={"center"}>
                  <Grid2 size={{ xs: 2 }}>
                    <Box
                      px={1}
                      sx={{
                        cursor: "pointer",
                      }}
                    >
                      <IconButton onClick={() => setOpenDrawer(true)}>
                        <MenuOutlined
                          fontSize="inherit"
                          sx={{ fontSize: 30 }}
                        />
                      </IconButton>
                    </Box>
                  </Grid2>
                  <Grid2 size={{ xs: 8 }} textAlign={"center"}>
                    <Box
                      onClick={() => navigate("/")}
                      sx={{ cursor: "pointer" }}
                    >
                      <img
                        src={logo}
                        // height={"100%"}
                        width={"85%"}
                        alt="simplerhome"
                        style={{ maxWidth: "400px" }}
                      />
                    </Box>
                  </Grid2>
                  <Grid2 size={{ xs: 2 }} display="flex" justifyContent="flex-end" pr={1}>
                    {checkIsAuth() && <NotificationBell size="small" />}
                  </Grid2>
                </Grid2>
              </Grid2>
              {/* mobile-only-end */}

              {/* desktop-only-(xl-lg-md) */}
              <Grid2
                container
                display={{
                  xl: "flex",
                  lg: "flex",
                  md: "flex",
                  sm: "none",
                  xs: "none",
                }}
                alignItems={"center"}
              >
                <Grid2 size={{ xl: 2, lg: 2, md: 2, sm: 2 }}>
                  <Link to={"/"}>
                    <img
                      src={logo}
                      height={"100%"}
                      width={"100%"}
                      alt="simplerhome"
                    />
                  </Link>
                </Grid2>
                <Grid2
                  size={{ xl: 10, lg: 10, md: 10, sm: 10 }}
                  justifyContent={"flex-end"}
                >
                  <Stack
                    direction={"row"}
                    spacing={{
                      xl: 10,
                      lg: 10,
                      md: 5,
                      sm: 2,
                    }}
                    justifyContent={"flex-end"}
                  >
                    {appMenuPageConst.map((v: any, index: number) => {
                      const isActive = isActiveRoute(v.navigateTo);
                      return (
                        <Box
                          key={index}
                          onClick={() => handelNavigate(v)}
                          display={"flex"}
                          alignItems={"center"}
                        >
                          <Typography
                            variant="body1"
                            fontWeight={isActive ? 700 : 500}
                            sx={{
                              cursor: v.navigateTo && "pointer",
                              color: isActive ? "primary.main" : "inherit",
                            }}
                          >
                            {v.header}
                          </Typography>
                        </Box>
                      );
                    })}
                    {/* My Properties dropdown - only shown when user has claimed properties */}
                    {hasClaimedProperties && (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box
                          onClick={handlePropertiesClick}
                          display={"flex"}
                          alignItems={"center"}
                          sx={{ cursor: "pointer" }}
                        >
                          {/* <Home sx={{ mr: 0.5, fontSize: 20 }} /> */}
                          <Typography
                            variant="body1"
                            fontWeight={500}
                          >
                            My Properties
                          </Typography>
                          <KeyboardArrowDown
                            sx={{
                              ml: 0.5,
                              transition: "transform 0.2s",
                              transform: propertiesMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                            }}
                          />
                        </Box>
                        <Menu
                          anchorEl={propertiesAnchorEl}
                          open={propertiesMenuOpen}
                          onClose={handlePropertiesClose}
                          MenuListProps={{
                            "aria-labelledby": "my-properties-button",
                          }}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                          sx={{
                            "& .MuiPaper-root": {
                              mt: 1,
                              minWidth: 200,
                              maxWidth: 350,
                            },
                          }}
                        >
                          {propertyCollections.map((property: any, idx: number) => (
                            <MenuItem
                              key={property?.propertyId?._id || idx}
                              onClick={() => handlePropertySelect(property)}
                            >
                              <LocationOn sx={{ mr: 1, fontSize: 18, color: "text.secondary" }} />
                              <Typography variant="body2" noWrap>
                                {getPropertyAddress(property)}
                              </Typography>
                            </MenuItem>
                          ))}
                        </Menu>
                      </Box>
                    )}
                    {checkIsAuth() && (
                      <Box display="flex" alignItems="center">
                        <NotificationBell />
                      </Box>
                    )}
                    {checkIsAuth() && (
                      <Box display="flex" alignItems="center">
                        <IconButton
                          onClick={() => navigate("/wishlist")}
                          sx={{ p: 0 }}
                        >
                          <Badge
                            badgeContent={wishlist?.length || 0}
                            color="primary"
                          >
                            <Favorite color="action" />
                          </Badge>
                        </IconButton>
                      </Box>
                    )}
                    <Box display="flex" alignItems="center">
                      {checkIsAuth() ? (
                        <>
                          <IconButton
                            onClick={handleAvatarClick}
                            sx={{ p: 0 }}
                            aria-controls={avatarMenuOpen ? "avatar-menu" : undefined}
                            aria-haspopup="true"
                            aria-expanded={avatarMenuOpen ? "true" : undefined}
                          >
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                bgcolor: "primary.main",
                              }}
                            >
                              <AccountCircle sx={{ fontSize: 36 }} />
                            </Avatar>
                          </IconButton>
                          <Menu
                            id="avatar-menu"
                            anchorEl={avatarAnchorEl}
                            open={avatarMenuOpen}
                            onClose={handleAvatarClose}
                            MenuListProps={{
                              "aria-labelledby": "avatar-button",
                            }}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "right",
                            }}
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "right",
                            }}
                            sx={{
                              "& .MuiPaper-root": {
                                mt: 1,
                                minWidth: 180,
                                borderRadius: 2,
                              },
                            }}
                          >
                            <MenuItem
                              onClick={() => {
                                handleAvatarClose();
                                navigate(String(APP_ROUTES.MY_ACCOUNT.pathname));
                              }}
                            >
                              <AccountCircle sx={{ mr: 1.5, fontSize: 20, color: "text.secondary" }} />
                              <Typography variant="body2">My Account</Typography>
                            </MenuItem>
                            {hasClaimedProperties && (
                              <MenuItem
                                onClick={() => {
                                  handleAvatarClose();
                                  navigate(String(APP_ROUTES.HOME_HISTORY.pathname));
                                }}
                              >
                                <HistoryIcon sx={{ mr: 1.5, fontSize: 20, color: "text.secondary" }} />
                                <Typography variant="body2">Home History</Typography>
                              </MenuItem>
                            )}
                            <Divider />
                            <MenuItem
                              onClick={() => {
                                handleAvatarClose();
                                handelLogout();
                              }}
                            >
                              <LogoutIcon sx={{ mr: 1.5, fontSize: 20, color: "text.secondary" }} />
                              <Typography variant="body2">Logout</Typography>
                            </MenuItem>
                          </Menu>
                        </>
                      ) : (
                        <Button
                          onClick={handelLogin}
                          variant="contained"
                          size="medium"
                          sx={{ p: "7px 25px" }}
                        >
                          <Typography variant="body1" sx={{ cursor: "pointer" }}>
                            Login
                          </Typography>
                        </Button>
                      )}
                    </Box>
                  </Stack>
                </Grid2>
              </Grid2>
            </Box>
          </AppBar>
        </Box>
      </FullPaddingWrapper>
    </Container>
  );
});

export default AppHeader;
